import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
    Box,
    Container,
    Grid,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    Chip,
    Button,
    TextField,
    Checkbox,
    FormGroup,
    FormControl,
    FormControlLabel,
    FormLabel,
    Card,
    CardContent,
    CardHeader
} from '@material-ui/core'
import ReactFlagsSelect from 'react-flags-select'
import AdminAPI from 'config/api/AdminAPI'
import Loader from 'components/general/Loader'
import ReactDropzoneCropper from 'components/general/ReactDropzoneCropper'
import config from 'config/api/Config'
import { Storage } from 'aws-amplify'
import { onError } from 'config/lib/errorLib'
import { productCategoryLabels, productCategories } from 'config/constants/Constants'
import countries from 'i18n-iso-countries'
import enLocale from 'i18n-iso-countries/langs/en.json'

countries.registerLocale(enLocale)

const useStyles = makeStyles(() => ({
    table: {
        minWidth: 650
    },
    logo: {
        width: 50,
        height: 50,
        borderRadius: '4px',
        marginRight: '1rem',
        '& img': {
            borderRadius: '4px !important' // override MUI default of 50%
        }
    },
    chip: {
        margin: '2px'
    },
    dialogField: {
        marginBottom: '1rem'
    },
    clickableRow: {
        cursor: 'pointer'
    },
    wrapCell: {
        whiteSpace: 'normal',
        wordBreak: 'break-word'
    }
}))

function Brands() {
    const classes = useStyles()
    const api = new AdminAPI()

    const [brands, setBrands] = useState([])
    const [loading, setLoading] = useState(true)
    const [buttonLoading, setButtonLoading] = useState(false)
    const [selectedBrand, setSelectedBrand] = useState(null)
    const [categoryState, setCategoryState] = useState({})
    const [solarPanelAttributes, setSolarPanelAttributes] = useState({ origin: '', blurb: '' })
    const [origin, setOrigin] = useState('')
    const [newBrandName, setNewBrandName] = useState('')
    const [image, setImage] = useState(null)
    const [imagePreview, setImagePreview] = useState(null)

    useEffect(() => {
        fetchBrands()
    }, [])

    async function fetchBrands() {
        try {
            const response = await api.getBrands()
            setBrands(response.sort((a, b) => a.brandName.localeCompare(b.brandName)))
            setLoading(false)
        } catch (e) {
            onError(e)
        }
    }

    const selectBrand = (brand) => {
        setSelectedBrand(brand)
        setCategoryState(
            productCategories.reduce((acc, cat) => {
                acc[cat] = brand.categories?.includes(cat) || false
                return acc
            }, {})
        )
        setSolarPanelAttributes(brand.attributes?.SolarPanel || { origin: '', blurb: '' })
        setOrigin(brand.attributes?.origin ?? brand.attributes?.SolarPanel?.origin ?? '')
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const saveBrand = async () => {
        setButtonLoading(true)
        try {
            if (image) {
                await saveImage(selectedBrand.brandName)
            }
            const categories = Object.entries(categoryState).filter(([, val]) => val).map(([key]) => key)
            const updates = {
                categories,
                attributes: {
                    ...selectedBrand.attributes,
                    origin,
                    ...(categoryState['SolarPanel'] && { SolarPanel: solarPanelAttributes })
                }
            }
            const updated = await api.updateBrand(selectedBrand.brandName, updates)
            setBrands(prev => prev.map(b => b.brandName === updated.brandName ? updated : b))
        } catch (e) {
            onError(e)
        }
        setButtonLoading(false)
    }

    const addNewBrand = async () => {
        setButtonLoading(true)
        try {
            const added = await api.addBrand(newBrandName)
            setBrands(prev => [...prev, added])
            setNewBrandName('')
        } catch (e) {
            onError(e)
        }
        setButtonLoading(false)
    }

    async function saveImage(brand) {
        try {
            // upload image to s3
            let logoPath = 'brands/' + brand
            console.log('logo path: ', logoPath)

            let imageUploaded = await Storage.put(logoPath, image, {
                contentType: image.type,
                level: 'public'
            })
            console.log('imageUploaded: ', imageUploaded)

            let logoUrl = `https://${config.s3.BUCKET}.s3.${config.s3.REGION}.amazonaws.com/public/${logoPath}`
            console.log('logoUrl: ', logoUrl)

            await api.updateBrandLogo(brand, logoUrl)
        } catch (e) {
            console.log('error uploading image: ', e)
            onError(e)
        }
    }

    async function addImage(file) {
        console.log('image: ', file)
        const preview = URL.createObjectURL(file)
        console.log('preview url: ', preview)
        setImage(file)
        setImagePreview(preview)
    }

    return (
        <Container maxWidth={false} component={Box} marginTop='2rem'>
            <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
                <Typography variant='h3'>Manage Brands</Typography>
                <Box display='flex'>
                    <TextField
                        label='New Brand Name'
                        variant='outlined'
                        size='small'
                        value={newBrandName}
                        onChange={(e) => setNewBrandName(e.target.value)}
                        style={{ marginRight: '1rem' }}
                    />
                    <Button
                        variant='outlined'
                        color='primary'
                        onClick={addNewBrand}
                        disabled={!newBrandName.trim() || buttonLoading}
                    >
                        {!buttonLoading ? 'Add Brand' : <Loader height={12} />}
                    </Button>
                </Box>
            </Box>

            {loading ? (
                <Box display='flex' justifyContent='center' alignItems='center' height='30vh'>
                    <Loader />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TableContainer component={Paper}>
                            <Table className={classes.table}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{color: '#32325d'}}><strong>Brand</strong></TableCell>
                                        <TableCell style={{color: '#32325d'}}><strong>Products</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {brands.map((brand) => (
                                        <TableRow
                                            key={brand.brandName}
                                            hover
                                            className={classes.clickableRow}
                                            onClick={() => selectBrand(brand)}
                                        >
                                            <TableCell>
                                                <Box display='flex' alignItems='center'>
                                                    {brand.logo ? (
                                                        <Avatar
                                                            variant='square'
                                                            src={brand.logo}
                                                            alt={brand.brandName}
                                                            className={classes.logo}
                                                        />
                                                    ) : (
                                                        <Avatar variant='square' className={classes.logo}>
                                                            {brand.brandName.charAt(0)}
                                                        </Avatar>
                                                    )}
                                                    <Typography>{brand.brandName}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell className={classes.wrapCell}>
                                                {(brand.categories || []).map((cat) => (
                                                    <Chip
                                                        key={cat}
                                                        label={productCategoryLabels[cat] || cat}
                                                        color='primary'
                                                        size='small'
                                                        className={classes.chip}
                                                    />
                                                ))}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        {selectedBrand && (
                            <Card>
                                <CardHeader
                                    subheader={
                                        <>
                                            <Box display='flex' style={{ justifyContent: 'space-between' }}>
                                                <Box>
                                                    <Box
                                                        component={Typography}
                                                        variant='h3'
                                                        marginBottom='0!important'
                                                    >
                                                        <Box component='span'>Update Brand - {selectedBrand.brandName}</Box>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </>
                                    }
                                    classes={{ root: classes.cardHeaderRoot }}
                                ></CardHeader>
                                <CardContent>
                                    <FormLabel>Logo</FormLabel>
                                    <FormControl
                                        variant='filled'
                                        component={Box}
                                        width='100%'
                                        style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}
                                    >
                                        {(selectedBrand.logo || imagePreview) && (
                                            <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' width='100%'>
                                                <img 
                                                    src={imagePreview || selectedBrand.logo }
                                                    alt='Brand Logo'
                                                    style={{marginBottom: '1rem', maxHeight: '120px', borderRadius: '4px'}}
                                                />
                                            </Box>
                                        )}
            
                                        <ReactDropzoneCropper
                                            updateImage={addImage}
                                            noPreview
                                            button
                                            uploadText={'Upload Logo'}
                                        />
                                    </FormControl>
                                    <Box>
                                        <FormLabel style={{marginBottom: '1rem'}}>Assign Products</FormLabel>
                                    </Box>
                                    {productCategories.map(cat => (
                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    checked={categoryState[cat] || false}
                                                    onChange={(e) =>
                                                        setCategoryState(prev => ({
                                                            ...prev,
                                                            [cat]: e.target.checked
                                                        }))
                                                    }
                                                    color='primary'
                                                />
                                            }
                                            label={<p style={{ margin: 0, padding: 0 }}>{productCategoryLabels[cat] || cat}</p>}
                                        />
                                    ))}

                                    <Box mt={2}>
                                        <FormGroup>
                                            <FormLabel>Brand Origin Country</FormLabel>
                                            <FormControl fullWidth>
                                                <ReactFlagsSelect
                                                    selected={origin}
                                                    onSelect={(code) => setOrigin(code)}
                                                    searchable
                                                />
                                            </FormControl>
                                        </FormGroup>
                                    </Box>

                                    {categoryState['SolarPanel'] && (
                                        <Box mt={2}>
                                            <Box>
                                                <FormLabel style={{marginBottom: '2rem'}}>Solar Panel Attributes</FormLabel>
                                            </Box>
                                            <FormGroup>
                                                <FormLabel>Solar Panel Blurb</FormLabel>
                                                <FormControl fullWidth>
                                                    <TextField
                                                        className={classes.dialogField}
                                                        //label='Blurb'
                                                        fullWidth
                                                        variant='outlined'
                                                        multiline
                                                        rows={3}
                                                        value={solarPanelAttributes.blurb}
                                                        onChange={(e) => setSolarPanelAttributes(prev => ({ ...prev, blurb: e.target.value }))}
                                                    />
                                                </FormControl>
                                            </FormGroup>
                                        </Box>
                                    )}

                                    <Box display='flex' justifyContent='flex-end'>
                                        <Button 
                                            color='primary' 
                                            variant='contained' 
                                            onClick={saveBrand}
                                            disabled={buttonLoading}
                                        >
                                            {!buttonLoading ? 'Update' : <Loader height={12} />}
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        )}
                    </Grid>
                </Grid>
            )}
        </Container>
    )
}

export default Brands